import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Back15Icon from '@/components/component-library/Icon/types/Back15';
import Forward15Icon from '@/components/component-library/Icon/types/Forward15';
import PreviousIcon from '@/components/component-library/Icon/types/Previous';
import NextIcon from '@/components/component-library/Icon/types/Next';
import PreviousRoundedIcon from '@/components/component-library/Icon/types/PreviousRounded';
import NextRoundedIcon from '@/components/component-library/Icon/types/NextRounded';
import PlayIcon from '@/components/component-library/Icon/types/Play';
import PauseIcon from '@/components/component-library/Icon/types/Pause';
import PlayRoundedIcon from '@/components/component-library/Icon/types/PlayRounded';
import PauseRoundedIcon from '@/components/component-library/Icon/types/PauseRounded';
import { classes } from '@/helpers/styling';
import useAudioReduxControls from '@/hooks/audio/useAudioReduxControls';
import useMusicPlayerState from '@/hooks/audio/useMusicPlayerState';
import useLiveAudioSeek from '@/hooks/audio/useLiveAudioSeek';
import {
  selectAudioReadyForSeek,
  selectCurrentShowIsPodcast,
  selectPlayPauseIsAvailable,
  selectPreviousTrackIsAvailable,
  selectNextTrackIsAvailable
} from '@/state/selectors/audio';

const selector = (state) => ({
  audioReadyForSeek: selectAudioReadyForSeek(state),
  currentShowIsPodcast: selectCurrentShowIsPodcast(state),
  disablePlayPause: !selectPlayPauseIsAvailable(state),
  disablePrevious: !selectPreviousTrackIsAvailable(state),
  disableNext: !selectNextTrackIsAvailable(state)
});

const MusicPlayerControls = ({
  className,
  previousClassName,
  playClassName,
  nextClassName,
  plus15ClassName,
  minus15ClassName,
  hidePodcastButtons,
  useRoundedButtons
}) => {
  const controls = useAudioReduxControls();
  const { paused } = useMusicPlayerState();
  const {
    audioReadyForSeek,
    currentShowIsPodcast,
    disablePlayPause,
    disablePrevious,
    disableNext
  } = useSelector(selector);
  const seek = useLiveAudioSeek();
  const removePodcastButtons = hidePodcastButtons || !currentShowIsPodcast;
  const playIcon = (useRoundedButtons ? <PlayRoundedIcon /> : <PlayIcon />);
  const pauseIcon = (useRoundedButtons ? <PauseRoundedIcon /> : <PauseIcon />);

  return (
    <div className={ classes(className, 'music-player-controls') }>
      { !removePodcastButtons && currentShowIsPodcast && (
        <div
          className={ classes(minus15ClassName, 'controls-button', { '--is-disabled': !audioReadyForSeek }) }
          onClick={ seek.handleSeekBackward15Seconds }
        >
          <Back15Icon />
        </div>
      ) }

      <div
        className={ classes(previousClassName, 'controls-button', { '--is-disabled': disablePrevious }) }
        onClick={ disablePrevious ? undefined : controls.handlePreviousTrack }
      >
        { useRoundedButtons ? <PreviousRoundedIcon /> : <PreviousIcon /> }
      </div>
      <div
        className={ classes(playClassName, 'controls-button', 'play-button', { '--is-disabled': disablePlayPause }) }
        onClick={ disablePlayPause ? undefined : controls.handlePlayOrPause }
      >
        {
          paused
            ? playIcon
            : pauseIcon
        }
      </div>

      <div
        className={ classes(nextClassName, 'controls-button', { '--is-disabled': disableNext }) }
        onClick={ disableNext ? undefined : controls.handleNextTrack }
      >
        { useRoundedButtons ? <NextRoundedIcon /> : <NextIcon /> }
      </div>

      { !removePodcastButtons && currentShowIsPodcast && (
        <div
          className={ classes(plus15ClassName, 'controls-button', { '--is-disabled': !audioReadyForSeek }) }
          onClick={ seek.handleSeekForward15Seconds }
        >
          <Forward15Icon />
        </div>
      ) }
    </div>
  );
};

MusicPlayerControls.propTypes = {
  className: PropTypes.string,
  previousClassName: PropTypes.string,
  playClassName: PropTypes.string,
  nextClassName: PropTypes.string,
  plus15ClassName: PropTypes.string,
  minus15ClassName: PropTypes.string,
  hidePodcastButtons: PropTypes.bool,
  useRoundedButtons: PropTypes.bool
};

MusicPlayerControls.defaultProps = {
  className: '',
  previousClassName: '',
  playClassName: '',
  nextClassName: '',
  plus15ClassName: '',
  minus15ClassName: '',
  hidePodcastButtons: false,
  useRoundedButtons: false
};

export default MusicPlayerControls;